import { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Progress,
  Row,
  Segmented,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  AppstoreOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  EyeOutlined,
  GlobalOutlined,
  PlusOutlined,
  ProjectOutlined,
  RobotOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { AgentFactoryOptions, getAgentFactoryOptions } from '../../services/agentFactoryApi';
import './ProjectList.css';

const { Title, Paragraph, Text } = Typography;

const PHASE_LABELS: Record<string, string> = { design: '设计', development: '开发', testing: '测试', deployment: '部署' };
const phaseColors: Record<string, string> = { design: '#1677ff', development: '#52c41a', testing: '#fa8c16', deployment: '#13c2c2' };
const PHASE_ORDER = ['design','development'];

const sanitizeProjectFolderName = (value: unknown) => String(value || '')
  .trim()
  .replace(/[^a-zA-Z0-9\u4e00-\u9fa5_-]/g, '_');

const normalizeWorkspacePath = (value: unknown) => String(value || '')
  .replace(/\\/g, '/')
  .replace(/\/+$/, '');

const deriveUserRootPath = (workbenches: any[]) => {
  const localWorkbench = workbenches.find((workbench: any) => workbench.type === 'local');
  if (!localWorkbench?.workPath) return '/etc_data/workspaces/';
  const workPath = normalizeWorkspacePath(localWorkbench.workPath);
  const match = workPath.match(/^(\/etc_data\/[^/]+)/);
  return `${match ? match[1] : workPath}/`;
};

const findWorkspaceConflict = (projects: any[], workspacePath: string) => {
  const normalizedPath = normalizeWorkspacePath(workspacePath);
  return projects.find((project: any) => (
    !project.deletedAt
    && normalizeWorkspacePath(project.workspacePath) === normalizedPath
  ));
};

const suggestAvailableFolderName = (projects: any[], rootPath: string, requestedName: string) => {
  const baseName = sanitizeProjectFolderName(requestedName) || 'project';
  if (!findWorkspaceConflict(projects, `${rootPath}${baseName}/`)) return baseName;
  for (let suffix = 2; suffix < 10_000; suffix += 1) {
    const candidate = `${baseName}-${suffix}`;
    if (!findWorkspaceConflict(projects, `${rootPath}${candidate}/`)) return candidate;
  }
  return `${baseName}-${Date.now().toString(36)}`;
};

const getApiErrorMessage = (error: any) => {
  const apiMessage = error?.response?.data?.message;
  if (Array.isArray(apiMessage)) return apiMessage.join('；');
  if (typeof apiMessage === 'string' && apiMessage.trim()) return apiMessage;
  return '创建失败';
};

export default function ProjectList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [wbCount, setWbCount] = useState(0);
  const [workbenches, setWorkbenches] = useState<any[]>([]);
  const [form] = Form.useForm();
  const [showDeleted, setShowDeleted] = useState(false);
  const [agentFactoryOptions, setAgentFactoryOptions] = useState<AgentFactoryOptions | null>(null);

  useEffect(() => {
    loadProjects();
    loadWorkbenches();
    getAgentFactoryOptions().then(setAgentFactoryOptions).catch(() => setAgentFactoryOptions(null));
  }, []);
  useEffect(() => {
    if (!modalVisible || !agentFactoryOptions) return;
    const requiredCapabilities = agentFactoryOptions.capabilities
      .filter(item => item.required)
      .map(item => item.value);
    const currentCapabilities = form.getFieldValue('capabilities') || [];
    form.setFieldValue('capabilities', Array.from(new Set([
      ...requiredCapabilities,
      ...currentCapabilities,
    ])));
  }, [agentFactoryOptions, modalVisible, form]);

  const loadWorkbenches = async () => {
    try { const r = await api.get('/dev-workspace/workbenches'); setWorkbenches(r.data||[]); setWbCount((r.data||[]).length); } catch {}
  };

  const loadProjects = async () => {
    setLoading(true);
    try { const r = await api.get('/projects'); setProjects(r.data || []); } catch { message.error('加载失败'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (values: any) => {
    if (creating) return;
    const localWbs = workbenches.filter((workbench: any) => workbench.type === 'local');
    if (localWbs.length === 0) {
      message.error('请先创建本地工作台');
      return;
    }
    const rootPath = deriveUserRootPath(localWbs);
    const folderName = sanitizeProjectFolderName(values.folderName || values.name);
    const workspacePath = `${rootPath}${folderName}/`;
    const conflict = findWorkspaceConflict(projects, workspacePath);
    if (conflict) {
      const suggestion = suggestAvailableFolderName(projects, rootPath, folderName);
      const conflictMessage = `目录已被项目「${conflict.name}」使用，请改用「${suggestion}」或直接进入已有项目`;
      form.setFields([{ name: 'folderName', errors: [conflictMessage] }]);
      message.error(conflictMessage);
      return;
    }

    setCreating(true);
    try {
      const payload: any = {
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        workspacePath,
      };
      if (values.projectKind === 'business_agent') {
        const requiredCapabilities = (agentFactoryOptions?.capabilities || [])
          .filter(item => item.required)
          .map(item => item.value);
        payload.settings = {
          projectKind: 'business_agent',
          industryPack: 'highway-cn',
          industryPackVersion: agentFactoryOptions?.version || '1.0.0',
          experienceChannels: values.experienceChannels,
          agentFactory: {
            schemaVersion: '1.0',
            domain: values.agentDomain,
            dataSources: values.dataSources,
            capabilities: Array.from(new Set([
              ...requiredCapabilities,
              ...(values.capabilities || []),
            ])),
            experienceChannels: values.experienceChannels,
            businessGoal: values.description,
            designStatus: 'draft',
            delivery: { status: 'not_started' },
          },
        };
      }
      const result = await api.post('/projects', payload);
      setModalVisible(false); form.resetFields();
      if (values.projectKind === 'business_agent') {
        message.success('智能体项目已创建，请用一句话补充或确认业务需求');
        navigate(`/dev-workspace?projectId=${result.data.id}&agentSetup=1`);
      } else {
        message.success('创建成功');
        loadProjects();
      }
    } catch (error: any) {
      const errorMessage = getApiErrorMessage(error);
      if (error?.response?.status === 400 && errorMessage.includes('工作区路径已存在')) {
        form.setFields([{ name: 'folderName', errors: [errorMessage] }]);
      }
      message.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const openCreateModal = () => {
    form.resetFields();
    const requiredCapabilities = (agentFactoryOptions?.capabilities || [])
      .filter(item => item.required)
      .map(item => item.value);
    form.setFieldsValue({
      projectKind: 'application',
      agentDomain: 'general',
      dataSources: ['documents', 'spreadsheets'],
      capabilities: requiredCapabilities,
      experienceChannels: ['standalone_web'],
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try { await api.delete(`/projects/${id}`); message.success('已删除（可恢复）'); loadProjects(); }
    catch { message.error('操作失败'); }
  };

  const handleRestore = async (id: string) => {
    try { await api.post(`/projects/${id}/restore`); message.success('已恢复'); loadProjects(); }
    catch { message.error('恢复失败'); }
  };

  const getProgress = (project: any) => {
    // 基于实际产出物计算进度，而非单纯按阶段
    const phaseIdx = PHASE_ORDER.indexOf(project.status);
    if (phaseIdx < 0) return 0;
    const reqCount = project._count?.requirements || 0;
    // design阶段：有需求文档才算开始
    if (phaseIdx === 0) return reqCount > 0 ? 10 : 0;
    // 后续阶段：阶段基础分 + 产出物加分
    return Math.round((phaseIdx / PHASE_ORDER.length) * 100);
  };

  const localWorkbench = workbenches.find((workbench: any) => workbench.type === 'local');
  // 从 workPath 推导用户根工作区（/etc_data/{username}/），而非项目子目录。
  const userRootPath = deriveUserRootPath(workbenches);
  const watchedFolderName = Form.useWatch('folderName', form);
  const watchedProjectKind = Form.useWatch('projectKind', form);
  const isAgentProject = watchedProjectKind === 'business_agent';

  return (
    <div style={{ padding: '4px 0' }}>
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <Card className="lg-glass-panel">
          <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
            <div>
              <Title level={3} className="lg-title-gradient" style={{ margin: 0 }}>项目管理</Title>
              <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>创建普通应用或行业智能体，并在设计、开发两个工作模式中持续协作。</Paragraph>
            </div>
            <Button type="primary" icon={<PlusOutlined />} className="lg-btn-primary" size="large" style={{ borderRadius: 16, height: 48 }}
              disabled={wbCount === 0}
              onClick={openCreateModal}>
              {wbCount === 0 ? '请先创建工作台' : '新建项目'}
            </Button>
            {wbCount === 0 && <Text type="secondary" style={{ display:'block',marginTop:8,fontSize:12 }}>请先在 <a onClick={()=>navigate('/dev-workspace')}>AI开发工作台</a> 中创建工作台</Text>}
          </Space>
        </Card>

        {!loading && projects.filter((p:any)=>!p.deletedAt).length === 0 && !showDeleted && (
          <Card className="lg-glass-card" style={{ textAlign: 'center', padding: 40 }}>
            <Empty description="暂无项目" />
            <Button type="primary" icon={<PlusOutlined />} style={{ marginTop: 16, borderRadius: 14 }} onClick={openCreateModal}>创建第一个项目</Button>
          </Card>
        )}

        {!showDeleted && (
          <Row gutter={[20, 20]}>
            {projects.filter((p:any) => !p.deletedAt).map((project) => (
            <Col xs={24} sm={12} xl={8} key={project.id}>
              <Card className="lg-glass-card" hoverable
                onClick={() => navigate(`/projects/${project.id}`)}
                style={{ cursor: 'pointer' }}>
                <Space direction="vertical" size={14} style={{ width: '100%' }}>
                  <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <Avatar icon={project.settings?.projectKind === 'business_agent' ? <RobotOutlined /> : <ProjectOutlined />} style={{ background: project.settings?.projectKind === 'business_agent' ? '#0f766e' : phaseColors[project.status] || '#1677ff' }} />
                      <div>
                        <Space size={6}>
                          <Text strong style={{ fontSize: 16 }}>{project.name}</Text>
                          {project.settings?.projectKind === 'business_agent' && <Tag color="cyan">智能体</Tag>}
                        </Space>
                        <div><Text type="secondary" style={{ fontSize: 12 }}>{project.description || project.workspacePath || '暂无描述'}</Text></div>
                      </div>
                    </Space>
                    <Tag color={phaseColors[project.status]} style={{ borderRadius: 8 }}>
                      {PHASE_LABELS[project.status] || project.status}
                    </Tag>
                  </Space>
                  <Progress percent={getProgress(project)} strokeColor={{ from: phaseColors[project.status] || '#1677ff', to: '#8b5cf6' }} size="small" showInfo={false} />
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space size={4}>
                      <ClockCircleOutlined style={{ fontSize: 12, color: '#94a3b8' }} />
                      <Text type="secondary" style={{ fontSize: 12 }}>{project.createdAt ? new Date(project.createdAt).toLocaleDateString('zh-CN') : '-'}</Text>
                    </Space>
                    <Space>
                      <Button type="link" size="small" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project.id}`); }}>查看</Button>
                      <Popconfirm title="确认删除？" onConfirm={(e) => { e?.stopPropagation(); handleDelete(project.id); }}>
                        <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()}>删除</Button>
                      </Popconfirm>
                    </Space>
                  </Space>
                </Space>
              </Card>
            </Col>
            ))}
          </Row>
        )}

        {/* 底部：查看/隐藏已删除项目 */}
        {projects.filter((p:any) => p.deletedAt).length > 0 && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button type="dashed" onClick={() => setShowDeleted(!showDeleted)}>
              {showDeleted ? '收起已删除项目' : `查看已删除项目 (${projects.filter((p:any) => p.deletedAt).length})`}
            </Button>
          </div>
        )}

        {showDeleted && (
          <Row gutter={[20, 20]} style={{ marginTop: 8 }}>
            {projects.filter((p:any) => p.deletedAt).map((project) => (
            <Col xs={24} sm={12} xl={8} key={project.id}>
              <Card className="lg-glass-card" style={{ opacity: 0.5 }}>
                <Space direction="vertical" size={14} style={{ width: '100%' }}>
                  <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <Avatar icon={<ProjectOutlined />} />
                      <div>
                        <Text strong style={{ fontSize: 16 }}>{project.name}</Text>
                        <Tag color="red" style={{marginLeft:8}}>已删除</Tag>
                        <div><Text type="secondary" style={{ fontSize: 12 }}>{project.description || project.workspacePath || ''}</Text></div>
                      </div>
                    </Space>
                  </Space>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>删除于 {project.deletedAt ? new Date(project.deletedAt).toLocaleDateString('zh-CN') : '-'}</Text>
                    <Button type="link" size="small" icon={<UndoOutlined />} onClick={(e) => { e.stopPropagation(); handleRestore(project.id); }}>恢复</Button>
                  </Space>
                </Space>
              </Card>
            </Col>
            ))}
          </Row>
        )}
      </Space>

      <Modal className="project-create-modal" title={null} open={modalVisible} onCancel={() => setModalVisible(false)} footer={null} width={isAgentProject ? 900 : 640}>
        <div className="df-form-modal-head">
          <div>
            <span className="df-form-modal-kicker">项目管理</span>
            <h2>新建项目</h2>
            <p>{isAgentProject ? '描述业务、选择数据和能力，智坊将按行业 Skill 完成设计、开发、验证与交付。' : '创建本地项目目录，在设计和开发模式中完成协作。'}</p>
          </div>
          <span className="df-form-modal-icon"><ProjectOutlined /></span>
        </div>
        <Form
          className="df-form-modal-body"
          form={form}
          layout="vertical"
          onFinish={handleCreate}
          initialValues={{ projectKind: 'application' }}
        >
          <Form.Item name="projectKind" label="要打造什么">
            <Segmented
              block
              options={[
                {
                  value: 'application',
                  label: <span className="project-kind-option"><AppstoreOutlined /><b>应用开发</b><small>沿用现有流程</small></span>,
                },
                {
                  value: 'business_agent',
                  disabled: agentFactoryOptions?.enabled === false,
                  label: <span className="project-kind-option"><RobotOutlined /><b>智能体开发</b><small>高速公路行业架构</small></span>,
                },
              ]}
            />
          </Form.Item>
          {isAgentProject && (
            <Alert
              className="agent-create-intro"
              type="info"
              showIcon
              message="您只需要说清业务需求"
              description="系统会自动完成数据架构选择、Skill 规划、语义路由、证据溯源、HTML 结果、自动测试和投产规范设计；方案确认前不会直接开发。"
            />
          )}
          <Form.Item name="name" label="项目名称" rules={[{ required: true }]}>
            <Input placeholder="请输入项目名称" />
          </Form.Item>
          <Form.Item
            name="description"
            label={isAgentProject ? '一句话业务需求' : '项目描述'}
            rules={isAgentProject ? [{ required: true, message: '请用一句话描述智能体要解决的业务问题' }] : []}
          >
            <Input.TextArea
              rows={isAgentProject ? 4 : 3}
              placeholder={isAgentProject
                ? '例如：做一个机电运维智能体，能结合设备状态和工单回答故障、人员负荷与处置建议，并且每个结论可以查看依据。'
                : '补充项目目标、业务背景或交付要求'}
            />
          </Form.Item>
          {isAgentProject && (
            <div className="agent-create-config">
              <div className="agent-create-section-head">
                <DatabaseOutlined />
                <div>
                  <b>业务与数据范围</b>
                  <span>用于选择正确的数据处理架构，后续仍可在对话中补充。</span>
                </div>
              </div>
              <div className="agent-create-grid">
                <Form.Item name="agentDomain" label="高速公路业务域" rules={[{ required: true }]}>
                  <Select
                    options={(agentFactoryOptions?.domains || []).map(item => ({
                      value: item.value,
                      label: item.label,
                      title: item.description,
                    }))}
                    placeholder="选择最接近的业务域"
                  />
                </Form.Item>
                <Form.Item
                  name="experienceChannels"
                  label="最终使用入口"
                  rules={[{ required: true, type: 'array', min: 1, message: '至少选择一个使用入口' }]}
                >
                  <Checkbox.Group
                    options={(agentFactoryOptions?.channels || []).map(item => ({
                      value: item.value,
                      label: item.label,
                    }))}
                  />
                </Form.Item>
              </div>
              <Form.Item
                name="dataSources"
                label="当前或计划接入的数据源"
                rules={[{ required: true, type: 'array', min: 1, message: '至少选择一种数据源' }]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  maxTagCount="responsive"
                  placeholder="可多选：门架流水、设备状态、文档、聊天信息等"
                  options={(agentFactoryOptions?.dataSources || []).map(item => ({
                    value: item.value,
                    label: `${item.label} · ${item.group}`,
                  }))}
                />
              </Form.Item>
              <div className="agent-create-section-head">
                <GlobalOutlined />
                <div>
                  <b>增值能力</b>
                  <span>自然语言、HTML 结果、溯源、纠错和自动测试为投产基础能力，已默认启用。</span>
                </div>
              </div>
              <Form.Item name="capabilities">
                <Checkbox.Group className="agent-capability-grid">
                  {(agentFactoryOptions?.capabilities || []).map(item => (
                    <Checkbox key={item.value} value={item.value} disabled={item.required}>
                      {item.label}{item.required && <Text type="secondary"> · 基础</Text>}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </Form.Item>
            </div>
          )}
          {localWorkbench && (
            <>
              <div className="df-form-path-card df-form-path-card-primary">
                <Text>当前工作区</Text>
                <div>
                  {userRootPath}
                </div>
              </div>
              <Form.Item
                name="folderName"
                label="项目目录名"
                validateFirst
                rules={[
                  { required: true, message: '请输入项目文件夹名称' },
                  {
                    validator: async (_, value) => {
                      const folderName = sanitizeProjectFolderName(value);
                      if (!folderName) throw new Error('项目目录名至少需要包含一个字母、数字、中文、短横线或下划线');
                      const conflict = findWorkspaceConflict(projects, `${userRootPath}${folderName}/`);
                      if (conflict) {
                        const suggestion = suggestAvailableFolderName(projects, userRootPath, folderName);
                        throw new Error(`目录已被项目「${conflict.name}」使用，建议改为「${suggestion}」`);
                      }
                    },
                  },
                ]}
              >
                <Input placeholder="例如：smart-road-project" />
              </Form.Item>
              <div className="df-form-path-card df-form-path-card-success">
                <Text>项目完整路径</Text>
                <div>
                  {userRootPath}{watchedFolderName || '项目目录名'}/
                </div>
              </div>
            </>
          )}
          <Form.Item className="df-form-modal-actions">
            <Button onClick={() => setModalVisible(false)}>取消</Button>
            <Button type="primary" htmlType="submit" className="lg-btn-primary" loading={creating}>创建项目</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
